import { CircularProgress, useTheme } from '@material-ui/core';
import { AirplaneIcon, PlusIconTwoTone } from '@room-match/shared-ui-components';
import AvatarSkeleton from 'components/Avatar/AvatarSkeleton/AvatarSkeleton';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useQueryClient } from 'react-query';
import { COLORS } from 'shared/constants/COLORS';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { RepresentationTypeGraphQL } from 'shared/enums/RepresentationTypeGraphQL';
import useImageLoader from 'shared/hooks/useImageLoader';
import { IProfileGraphQL } from 'shared/interfaces/IProfile';
import { profileService } from 'shared/services/profileService';
import {
  AddCont,
  Avatar,
  ItemAgency,
  ItemCompletion,
  ItemContact,
  ItemDivision,
  ItemInfo,
  ItemName,
  MakePrimaryButton,
  PrivateCD,
  ProfileItemCont,
} from './Profiles.styles';
import user from './user.png';
const { updateProfile } = profileService();
const { DARK_CONTAINER_FIELDS_ON_CARD, WARNING_CONTAINER } = COLORS;
interface Props {
  profile?: IProfileGraphQL;
  addModal?: VoidFunction;
  click?: (id: string) => void;
  primaryId: string;
  handleMakePrimary: (profileId: string, isPrimary: boolean, image: string | undefined) => void;
  isLoading?: boolean;
}

function ProfileItem({ profile, addModal, click, primaryId, handleMakePrimary, isLoading }: Props) {
  const theme = useTheme();
  const [percentage, setPercentage] = useState(0);
  const queryClient = useQueryClient();
  const { mutateAsync } = updateProfile();
  const [loading, setLoading] = useState(false);
  const { imageLoaded, noProfileImage, error } = useImageLoader(profile?.primary_medium_attachment?.attachment || '');
  useEffect(() => {
    if (profile === undefined) return;
    let total = 0;
    const videos = profile.videos?.length || 0;
    const images = profile.images?.length || 0;
    const resume = profile.resume?.length || 0;

    if (images > 2) {
      total = total + 60;
    } else if (images === 2) {
      total = total + 40;
    } else if (images === 1) {
      total = total + 20;
    }

    if (videos > 0) total = total + 20;
    if (resume > 0) total = total + 20;

    setPercentage(profile?.completeness || total);
  }, [profile]);

  const color = () => {
    if (percentage === 100) {
      return '#469c1d';
    } else {
      if (percentage < 50) {
        return '#e34343';
      } else {
        return '#daa13e';
      }
    }
  };

  const isPrimary = profile?.primary;
  const awayDate = moment(profile?.talent?.away_period?.end_datetime).format('MMM DD');

  return (
    <ProfileItemCont onClick={() => profile && click && click(profile?.id || '')}>
      {profile?.talent?.away_period ? (
        <div
          style={{
            borderRadius: 8,
            left: 0,
            top: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: 100,
            background: DARK_CONTAINER_FIELDS_ON_CARD,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <AirplaneIcon fontSize="small" htmlColor="white" />
          {awayDate}
        </div>
      ) : null}
      {!profile ? (
        <AddCont onClick={addModal}>
          <PlusIconTwoTone />
          <span style={{ marginTop: 5 }}>Add a new Representative</span>
        </AddCont>
      ) : (
        <>
          <MakePrimaryButton
            disabled={isLoading}
            isPrimary={isPrimary}
            loading={isLoading}
            onClick={(e: any) => {
              e.stopPropagation();
              handleMakePrimary(profile?.id, isPrimary || false, profile.primary_medium_attachment?.attachment);
            }}
          >
            {isLoading ? (
              <CircularProgress
                style={{
                  width: 14,
                  height: 14,
                  margin: 4,
                }}
              />
            ) : isPrimary ? (
              'Primary'
            ) : (
              'Make Primary'
            )}
          </MakePrimaryButton>

          {imageLoaded ? (
            <Avatar
              src={!error ? `${profile.primary_medium_attachment?.attachment || user}/70x90` : noProfileImage}
              alt="avatar"
            />
          ) : (
            <AvatarSkeleton style={{ width: 70, height: 90, alignSelf: 'center' }} />
          )}

          <ItemInfo>
            {profile.casting_private ? (
              <div
                style={{
                  padding: '2px 10px',
                  width: 'fit-content',
                  borderRadius: 2,
                  background: WARNING_CONTAINER,
                  marginBottom: 2,
                }}
              >
                <CustomTypography fontSize={12} fontWeight={500}>
                  PRIVATE
                </CustomTypography>
              </div>
            ) : (
              <React.Fragment />
            )}
            <ItemName>{profile.talent.stage_name || ''}</ItemName>

            {profile?.representation_type === RepresentationTypeGraphQL.FREELANCE ? (
              profile?.casting_private ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <PrivateCD>{profile.casting_company.name}</PrivateCD>
                  <ItemContact>{profile.casting_company.contact_number}</ItemContact>
                </div>
              ) : (
                <>
                  <ItemAgency>Non-Represented</ItemAgency>
                  {profile?.agency?.contact_number ? (
                    <ItemContact>{profile?.agency?.contact_number}</ItemContact>
                  ) : null}
                </>
              )
            ) : (
              <>
                <ItemAgency>{profile?.agency?.full_name || profile?.agency?.name}</ItemAgency>
                {profile.division ? <ItemDivision>{profile.division.name}</ItemDivision> : <></>}
                {profile?.agency?.contact_number ? <ItemContact>{profile?.agency?.contact_number}</ItemContact> : null}
                {profile?.agency?.country ? <ItemContact>{profile?.agency?.country}</ItemContact> : null}
                {profile?.agency?.state ? <ItemContact>{profile?.agency?.state}</ItemContact> : null}
              </>
            )}
          </ItemInfo>
          <ItemCompletion>
            <CircularProgressbar
              value={percentage}
              text={`${percentage}%`}
              styles={{
                path: { stroke: color() },
                text: { fill: theme.palette.type === 'dark' ? 'white' : 'black' },
                trail: { stroke: theme.palette.type === 'dark' ? '#2e333a' : 'lightgray' },
              }}
            />
          </ItemCompletion>
        </>
      )}
    </ProfileItemCont>
  );
}

export default ProfileItem;
